import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { Order } from '../../shared/models/order.model';
import { TranslateService } from '@ngx-translate/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { OrderService } from '../../shared/services/order.service';
import { AuthService } from "../../shared/services/auth.service";
import { RolesEnum } from "../../shared/enums/roles.enum";
import { countryCodes } from "../../shared/models/country-code.model";
import { WebOrderModel } from "../../shared/models/web-order.model";
import { WebOrderService } from "../../shared/services/web-order.service";

@Component({
    selector: 'app-edit-order',
    templateUrl: './edit-order.component.html',
    styleUrls: ['./edit-order.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditOrderComponent implements OnChanges {
  @Input() order: Order;
  @Output() edited = new EventEmitter<void>();
  webOrder: WebOrderModel;
  public orderForm: FormGroup = null;
  public invoiceAddressForm: FormGroup = null;
  public progress = false;
  public RolesEnum = RolesEnum;
  protected readonly countryCodes = countryCodes;
  public webOrderService = inject(WebOrderService);
  public translate = inject(TranslateService);
  public authService = inject(AuthService);
  public orderService = inject(OrderService);

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.order) {
      if (!this.order.webOrderId) {
        this.initEditOrderForm();
      } else {
        this.getWebOrder(this.order.webOrderId).then(() => this.initEditOrderForm());
      }
    }
  }

  /**
   * Create or update order
   */
  async save(): Promise<void> {
    this.startUpdate();
    const order = this.orderForm.getRawValue();
    this.order.emailIsActive = order.emailIsActive;
    this.order.disabled = order.disabled;
    if (this.order.webOrderId) {
      await this.webOrderService.update(this.webOrder.id,
        { invoicing: this.invoiceAddressForm.getRawValue() });
    }
    await this.orderService.update(this.order.id, {
      emailIsActive: this.order.emailIsActive,
      disabled: this.order.disabled,
    });
    this.endUpdate()
  }

  /**
   * Update process states
   */
  private startUpdate(): void {
    this.progress = true;
    this.orderForm.controls.emailIsActive.disable();
  }

  /**
   * Update process states
   */
  public endUpdate(): void {
    this.progress = false;
    this.orderForm.controls.emailIsActive.enable();
    this.edited.emit();
  }

  /**
   * Initialize the form to edit the order
   */
  private initEditOrderForm(): void {
    this.orderForm = new FormGroup({
      id: new FormControl(this.order.id, { nonNullable: true, validators: [Validators.required] }),
      emailIsActive: new FormControl(this.order.emailIsActive,
        { nonNullable: true, validators: [Validators.required] }),
      disabled: new FormControl(this.order.disabled, { nonNullable: true, validators: [Validators.required] }),
    });
    if (this.webOrder)
      this.invoiceAddressForm = new FormGroup({
        name: new FormControl(this.webOrder?.invoicing?.name ?? '',
          { nonNullable: true, validators: [Validators.required] }),
        company: new FormControl(this.webOrder?.invoicing?.company ?? '',
          { nonNullable: true, validators: [Validators.required]  }),
        street1: new FormControl(this.webOrder?.invoicing?.street1 ?? '',
          { nonNullable: true, validators: [Validators.required] }),
        street2: new FormControl(this.webOrder?.invoicing?.street2 ?? '', { nonNullable: true }),
        city: new FormControl(this.webOrder?.invoicing?.city ?? '',
          { nonNullable: true, validators: [Validators.required] }),
        state: new FormControl(this.webOrder?.invoicing?.state ?? '', { nonNullable: true }),
        zip: new FormControl(this.webOrder?.invoicing?.zip ?? '',
          { nonNullable: true, validators: [Validators.required] }),
        country: new FormControl(this.webOrder?.invoicing?.country ?? '',
          { nonNullable: true, validators: [Validators.required] }),
        phone: new FormControl(this.webOrder?.invoicing?.phone ?? '',
          { nonNullable: true, validators: [Validators.required]  }),
        email: new FormControl(this.webOrder?.invoicing?.email ?? '',
          { nonNullable: true, validators: [Validators.required]  }),
      });
  }

  async getWebOrder(webOrderId: string): Promise<void> {
    this.webOrder = await this.webOrderService.get(webOrderId);
  }
}
